import React from "react";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../app/store/slices/uiSlice";
import { XMarkIcon } from "@heroicons/react/24/outline";

const SearchBar = ({
  value,
  onChange,
  placeholder = "Search...",
  showClearButton = true,
  className = "",
  onClear,
  disabled = false,
  size = "default", // "sm", "default", "lg"
  variant = "default", // "default", "bordered", "filled"
  showSearchIcon = true,
  onFocus,
  onBlur,
  autoComplete = "off",
  debounceMs = 0, // Optional debouncing
}) => {
  const darkMode = useSelector(selectDarkMode);
  const [debounceTimer, setDebounceTimer] = React.useState(null);

  const handleClear = () => {
    onChange("");
    if (onClear) {
      onClear();
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value.trim();

    if (debounceMs > 0) {
      // Clear existing timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Set new timer
      const timer = setTimeout(() => {
        onChange(newValue);
      }, debounceMs);

      setDebounceTimer(timer);
    } else {
      // Immediate update
      onChange(newValue);
    }
  };

  // Clean up timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  // Size classes
  const sizeClasses = {
    sm: "py-1.5 text-sm",
    default: "py-2.5",
    lg: "py-3.5 text-lg",
  };

  // Variant classes
  const variantClasses = {
    default: darkMode ? "border border-gray-600 bg-gray-700 text-white placeholder-gray-400" : "border border-gray-200 bg-white",
    bordered: darkMode ? "border-2 border-gray-600 bg-gray-700 text-white placeholder-gray-400" : "border-2 border-gray-300 bg-white",
    filled: darkMode ? "border border-gray-600 bg-gray-800 text-white placeholder-gray-400" : "border border-gray-200 bg-gray-50",
  };

  // Icon positioning based on size
  const iconClasses = {
    sm: "h-4 w-4 left-2.5 top-2",
    default: "h-5 w-5 left-3 top-3",
    lg: "h-6 w-6 left-3.5 top-3.5",
  };

  // Padding based on icons
  const paddingLeft = showSearchIcon ? "pl-10" : "pl-4";
  const paddingRight = showClearButton && value ? "pr-10" : "pr-4";

  return (
    <div className={`flex-1 relative ${className}`}>
      <div className="relative">
        {/* Search Icon */}
        {showSearchIcon && (
          <svg className={`${iconClasses[size]} absolute ${darkMode ? "text-gray-400" : "text-gray-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        )}

        {/* Input Field */}
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className={`
            w-full ${paddingLeft} ${paddingRight} ${sizeClasses[size]}
            ${variantClasses[variant]} rounded-lg
            focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20
            focus:outline-none transition-all duration-200
            ${disabled ? (darkMode ? "bg-gray-800 cursor-not-allowed text-gray-500" : "bg-gray-100 cursor-not-allowed text-gray-500") : ""}
          `}
        />

        {/* Clear Button */}
        {showClearButton && value && !disabled && (
          <button
            onClick={handleClear}
            className={`
              absolute right-3 transition-colors
              ${darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-400 hover:text-gray-600"}
              ${size === "sm" ? "top-2" : size === "lg" ? "top-3.5" : "top-3"}
            `}
            title="Clear search"
            type="button"
          >
            <XMarkIcon className={size === "sm" ? "h-4 w-4" : size === "lg" ? "h-6 w-6" : "h-4 w-4"} />
          </button>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
